import { InputField } from '@/components/form/InputField';
import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Form } from '@/components/primitives/Form';
import { manageBookingSearchLoadingSelector } from '@/features/loading/loadingSelectors';
import { useBookingSearch } from '@/hooks/useBookingSearch';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { parseManageBookingUrlParams } from '@/utils/manageBookingUrlParams';
import {
  createFindBookingSchema,
  type FindBookingValues,
} from '@/utils/zodSchema';
import { Button, Card, Typography } from '@fleet/webshop-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';

const BookingSearch = () => {
  const isPending = useSelector(manageBookingSearchLoadingSelector);
  const { t } = useTranslation();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  usePageTitle('manageBooking');

  const form = useForm<FindBookingValues>({
    mode: 'onBlur',
    resolver: zodResolver(createFindBookingSchema(t)),
    defaultValues: {
      email: '',
      bookingNumber: '',
    },
  });

  const onSubmit = useBookingSearch();

  // Deep-link support: when the user arrives at /manage-booking with `email` and
  // `bookingNumber` query params (e.g. from a Salesforce link or the customer's own
  // "manage booking" form), pre-fill the form and auto-run the search so they land
  // on the booking details page. The search only auto-fires when both params are
  // present and valid; otherwise we just pre-fill and let the user complete it.
  // Mirrors the journey-search auto-submit pattern in SearchWidgetWrapper.
  const hasAutoSubmitted = useRef(false);

  useEffect(() => {
    if (hasAutoSubmitted.current) {
      return;
    }
    hasAutoSubmitted.current = true;

    const { email, bookingNumber } = parseManageBookingUrlParams();

    if (!email && !bookingNumber) {
      return;
    }

    form.reset({ email: email ?? '', bookingNumber: bookingNumber ?? '' });

    const parsed = createFindBookingSchema(t).safeParse({
      email,
      bookingNumber,
    });

    if (parsed.success) {
      onSubmit(parsed.data);
    }
  }, [form, onSubmit, t]);

  return (
    <CenteredLayout>
      <div className="mx-auto flex w-full max-w-[410px] flex-col gap-8 p-4 tablet:my-auto laptop:p-0">
        <Typography
          variant={isLaptopOrBigger ? 'title' : 'mobile-title'}
          className="text-left"
          as="h1"
        >
          <TransText i18nKey="manageBooking" />
        </Typography>

        <Card className="rounded-xl">
          <Form
            form={form}
            onSubmit={form.handleSubmit(onSubmit)}
            className={cn('flex flex-col gap-3 [&_input]:h-12', {
              '[&_input]:h-8': isLaptopOrBigger,
            })}
          >
            <InputField
              name="email"
              required
              placeholder={t('text.enterYourEmailAddress')}
              label={<TransText i18nKey="emailUsedForBooking" />}
            />
            <InputField
              name="bookingNumber"
              required
              placeholder={t('text.enterYourBookingNumber')}
              label={<TransText i18nKey="bookingNumber" />}
            />
            <Button
              size="medium"
              className="mt-3"
              data-testid="find-booking-btn"
              type="submit"
              isPending={isPending}
            >
              <TransText i18nKey="findYourBooking" />
            </Button>
          </Form>
        </Card>
      </div>
    </CenteredLayout>
  );
};

export default BookingSearch;
